import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Grid,
  Menu,
  Select,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import useStyles from "./style";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconChevronDown,
  IconDotsVertical,
  IconFileDescription,
  IconFiles,
  IconEdit,
  IconMapPin,
  IconPhone,
  IconStethoscope,
  IconTrash,
  IconUnlink,
} from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { stateZipCodes } from "../../../utils/zipCodes";
import { EHR_OPTIONS } from "../../../utils/ehrOptions";
import { useEffect, useState } from "react";
import {
  deleteSkypointProvider,
  editSkypointProvider,
  getProviderSettings,
  inviteSkypointProvider,
  resendProviderInvitation,
  unlinkSkypointProvider,
} from "../../../services/skypointProvider";
import swal from "sweetalert";
import { forgetPasswordSkypoint } from "../../../services";
import ProviderDocuments from "../../../components/ProviderDocuments";
import { isStaff } from "../../../http";

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const SkypointAddProvider = () => {
  const { profile } = useSelector((state) => state?.profile);
  const { providerSettings } = useSelector((state) => state.skypointProviders);
  const ehrOptions = providerSettings?.ehrOptions?.length ? providerSettings.ehrOptions : EHR_OPTIONS;
  const requireEhr = providerSettings?.requireEhr !== false;
  const requireNpi = !!providerSettings?.requireNpi;
  const { classes, cx } = useStyles();
  const { mode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isViewMode = mode === "view";

  useEffect(() => {
    if (!providerSettings) getProviderSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { state } = useLocation();
  const data = state?.data;
  // The provider list's "Documents" row action deep-links to that tab, so the
  // opening tab comes from the navigation state when one was passed.
  const [activeTab, setActiveTab] = useState(state?.tab || "overview");
  const isExternalProvider = !!data?.isExternal;

  const form = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      businessName: "",
      email: "",
      phone: "",
      address: "",
      address2: "",
      city: "",
      zipcode: "",
      state: "",
      npi: "",
      ehrSystem: "",
      ehrSystemOther: "",
      providerNotes: "",
    },

    validate: {
      firstName: (value) => (value ? null : "Required"),
      lastName: (value) => (value ? null : "Required"),
      businessName: (value) => (value ? null : "Required"),
      email: (value) => {
        if (!value) return "Required";
        return /^\S+@\S+$/.test(value) ? null : "Invalid email format";
      },
      phone: (value) => (value ? null : "Required"),
      address: (value) => (value ? null : "Required"),
      zipcode: (value) => (value ? null : "Required"),
      state: (value) => (value ? null : "Required"),
      npi: (value) => {
        if (!value) return requireNpi ? "Required" : null;
        return /^\d{10}$/.test(value) ? null : "NPI must be 10 digits";
      },
      ehrSystem: (value) =>
        isExternalProvider && requireEhr && !value ? "Required" : null,
      ehrSystemOther: (value, values) =>
        isExternalProvider && values.ehrSystem === "Other" && !value
          ? "Required"
          : null,
    },
  });

  const onBack = () => {
    navigate("/home/provider-skypoint");
  };

  // Adding a provider is the Add Provider drawer's job (see
  // components/AddProviderDrawer) — this page keeps only the prospect invite,
  // edit and view modes. The legacy full-page add form was a second, diverging
  // design for the same task, so /add now hands off to the drawer rather than
  // rendering it; the redirect exists because the URL can still be typed or
  // sit in someone's history.
  useEffect(() => {
    if (mode === "add") {
      navigate("/home/provider-skypoint", { replace: true, state: { openAdd: true } });
      return;
    }
    if (
      (profile?.user?.type === "staff" &&
        profile?.user?.role === "staff" &&
        mode !== "view") ||
      (mode === "prospect-add" && !data)
    ) {
      navigate("/home/provider-skypoint");
    }
  }, [data]);


  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const { email, ehrSystem, ehrSystemOther, ...rest } = values;
      const resolvedEhrSystem =
        ehrSystem === "Other" ? ehrSystemOther?.trim() : ehrSystem;

      if (mode === "prospect-add") {
        const payload = {
          ...rest,
          name: `${form?.values?.firstName} ${form?.values?.lastName}`.trim(),
          platform: "ogiso",
          id: data?.id || data?._id,
        };
        const res = await inviteSkypointProvider(payload);
        res && onBack();
      } else if (mode === "edit") {
        const payload = {
          ...rest,
          ...(isExternalProvider ? { ehrSystem: resolvedEhrSystem } : {}),
          name: `${form?.values?.firstName} ${form?.values?.lastName}`.trim(),
          id: data?.id || data?._id,
        };
        const res = await editSkypointProvider(payload);
        res && onBack();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      const knownEhr = EHR_OPTIONS.includes(data?.ehrSystem)
        ? data?.ehrSystem
        : data?.ehrSystem
        ? "Other"
        : "";
      form.setValues({
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        businessName: data?.businessName || "",
        email: data?.email || "",
        phone: data?.phone || "",
        address: data?.address || "",
        address2: data?.address2 || "",
        city: data?.city || "",
        zipcode: data?.zipcode || "",
        state: data?.state || "",
        npi: data?.npi || "",
        providerNotes: data?.providerNotes || "",
        ehrSystem: knownEhr,
        ehrSystemOther: knownEhr === "Other" ? data?.ehrSystem || "" : "",
      });
    }
  }, [data]);

  const handleCancel = () => {
    onBack();
  };

  // Only an external provider — a record this agency created — is deletable.
  // An Ogiso provider keeps their own account; the most we can do is unlink
  // them from Skypoint (see handleRemoveFromSkypoint below).
  const deleteSkypointProviderHandle = async () => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) {
      deleteSkypointProvider(data?.id);
      onBack();
    }
  };

  const handleRemoveFromSkypoint = async () => {
    const confirmed = await swal({
      title: "Remove Provider?",
      text: "This will remove the provider from your list. Their Ogiso account will not be deleted, and you can add them again later.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Remove"],
    });
    if (confirmed) {
      const success = await unlinkSkypointProvider(data?.id);
      if (success) {
        onBack();
      }
    }
  };

  const resetPassword = async () => {
    const email = await swal({
      title: `Reset Password`,
      text: "Do you want to email reset password link?",
      dangerMode: false,
      buttons: ["Cancel", "Send"],
    });
    if (email) {
      forgetPasswordSkypoint({ email: data?.email }, "reset");
    }
  };

  const resendInvitation = async () => {
    const confirmed = await swal({
      title: `Resend Invitation`,
      text: "Do you want to resend the invitation email?",
      dangerMode: false,
      buttons: ["Cancel", "Send"],
    });
    if (confirmed) {
      resendProviderInvitation(data?.id || data?._id);
    }
  };

  return (
    <Box className={classes.content}>
      <AppNavbar title="Providers" profile={profile} />
      <Box px={20}>
      {(mode === "add" || mode === "prospect-add") && (
        <>
        <ActionIcon unstyled className={classes.back} onClick={onBack}>
          <IconArrowLeft size={20} />
          <Text span>Back</Text>
        </ActionIcon>
        <Text mt={24} fz={16} fw={600} color="#171717" ff="Inter" p={4}>
          {mode === "add" ? "Add Provider" : "Invite Provider"}
        </Text>

        <Box className={classes.formBox}>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Grid maw={730} w="100%" gutter={16} mx="auto">
              <Grid.Col span={6}>
                <TextInput
                  label="Admin First Name"
                  placeholder="Enter First Name"
                  className={classes.inputField}
                  {...form.getInputProps("firstName")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Admin Last Name"
                  placeholder="Enter Last Name"
                  className={classes.inputField}
                  {...form.getInputProps("lastName")}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Business Name"
                  placeholder="Enter Business Name"
                  className={classes.inputField}
                  {...form.getInputProps("businessName")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Email"
                  placeholder="Enter Email"
                  className={classes.inputField}
                  readOnly={mode === "prospect-add"}
                  {...form.getInputProps("email")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone Number"
                  placeholder="Enter Phone Number"
                  className={classes.inputField}
                  {...form.getInputProps("phone")}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Address 1"
                  placeholder="Enter Address 1"
                  className={classes.inputField}
                  {...form.getInputProps("address")}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Address 2"
                  placeholder="Enter Address 2 (Optional)"
                  className={classes.inputField}
                  {...form.getInputProps("address2")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="City"
                  placeholder="Enter City"
                  className={classes.inputField}
                  {...form.getInputProps("city")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Zipcode"
                  placeholder="Enter Zipcode"
                  className={classes.inputField}
                  {...form.getInputProps("zipcode")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="State"
                  rightSection={<IconChevronDown color={"#94A3B8"} size={20} />}
                  rightSectionWidth={30}
                  placeholder="Select State"
                  className={classes.inputField}
                  data={Array.from(
                    new Set(stateZipCodes?.map((obj) => obj.ste_name))
                  )
                    .sort()
                    .map((ste_name) => ({
                      value: ste_name,
                      label: ste_name,
                    }))}
                  searchable
                  {...form.getInputProps("state")}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="NPI # (National Provider Identifier)"
                  placeholder="Enter 10-digit NPI Number"
                  className={classes.inputField}
                  {...form.getInputProps("npi")}
                />
              </Grid.Col>
              {isExternalProvider && (
                <>
                  <Grid.Col span={form.values.ehrSystem === "Other" ? 6 : 12}>
                    <Select
                      label="EHR System"
                      rightSection={<IconChevronDown color={"#94A3B8"} size={20} />}
                      rightSectionWidth={30}
                      placeholder="Select EHR"
                      className={classes.inputField}
                      data={ehrOptions.map((ehr) => ({ value: ehr, label: ehr }))}
                      {...form.getInputProps("ehrSystem")}
                    />
                  </Grid.Col>
                  {form.values.ehrSystem === "Other" && (
                    <Grid.Col span={6}>
                      <TextInput
                        label="EHR Name"
                        placeholder="Enter EHR system name"
                        className={classes.inputField}
                        {...form.getInputProps("ehrSystemOther")}
                      />
                    </Grid.Col>
                  )}
                </>
              )}
            </Grid>
            <Flex className={classes.btnGroup}>
              <Button
                variant="outline"
                className={classes.cancelBtn}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                className={classes.createBtn}
              >
                {mode === "add" ? "Add Provider" : "Invite Provider"}
              </Button>
            </Flex>
          </form>
        </Box>
        </>
      )}

      {(mode === "edit" || mode === "view") && (
        <>
          <button type="button" className={classes.crumbBack} onClick={onBack}>
            <IconArrowLeft size={15} />
            Back to providers
          </button>

          <Box className={classes.profHead}>
            <Box className={classes.profAv}>
              {initialsOf(data?.businessName || data?.name || "Provider")}
            </Box>
            <Box className={classes.profInfo}>
              <Box className={classes.profNameRow}>
                <Text className={classes.profName}>
                  {data?.businessName || data?.name || "Provider"}
                </Text>
                <span
                  className={`${classes.pill} ${
                    isExternalProvider ? classes.pillExternal : classes.pillOgiso
                  }`}
                >
                  {isExternalProvider ? "External" : "Ogiso"}
                </span>
              </Box>
              <Box className={classes.profMeta}>
                <span>
                  <IconStethoscope size={14} />
                  EHR: {isExternalProvider ? data?.ehrSystem || "—" : "Ogiso"}
                </span>
                {(data?.city || data?.state) && (
                  <span>
                    <IconMapPin size={14} />
                    {[data?.city, data?.state].filter(Boolean).join(", ")}
                  </span>
                )}
                {data?.phone && (
                  <span>
                    <IconPhone size={14} />
                    {data.phone}
                  </span>
                )}
              </Box>
            </Box>
            {profile?.user?.type !== "staff" && (
              <Box className={classes.profActions}>
                <Menu shadow="md" position="bottom-end" zIndex={1000}>
                  <Menu.Target>
                    <ActionIcon className={classes.kebabSquare} aria-label="Provider actions">
                      <IconDotsVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      icon={<IconEdit size={14} />}
                      onClick={() =>
                        navigate(`/home/provider-skypoint/edit`, { state: { data } })
                      }
                    >
                      Edit provider
                    </Menu.Item>
                    {/* Already on the page that holds the document store, so
                        this just moves to that tab rather than navigating. */}
                    <Menu.Item
                      icon={<IconFiles size={14} />}
                      onClick={() => setActiveTab("documents")}
                    >
                      Upload documents
                    </Menu.Item>
                    {/* An external provider documents in its own EHR, so the
                        only quarterly review it can have is the hand-entered
                        one — jump straight into creating it for this provider. */}
                    {isExternalProvider && (
                      <Menu.Item
                        icon={<IconFileDescription size={14} />}
                        onClick={() =>
                          navigate("/home/report-skypoint", {
                            state: { createManual: true, providerId: data?.id || data?._id },
                          })
                        }
                      >
                        New manual report
                      </Menu.Item>
                    )}
                    {!isExternalProvider && (
                      <>
                        <Menu.Item onClick={resendInvitation}>
                          Resend Invitation Email
                        </Menu.Item>
                        <Menu.Item onClick={resetPassword}>Reset Password</Menu.Item>
                      </>
                    )}
                    <Menu.Divider />
                    {isExternalProvider ? (
                      <Menu.Item
                        icon={<IconTrash size={14} />}
                        color="red"
                        onClick={deleteSkypointProviderHandle}
                      >
                        Delete provider
                      </Menu.Item>
                    ) : (
                      <Menu.Item
                        icon={<IconUnlink size={14} />}
                        color="red"
                        onClick={handleRemoveFromSkypoint}
                      >
                        Remove from Skypoint
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Box>
            )}
          </Box>

          <Tabs
            value={activeTab}
            onTabChange={setActiveTab}
            className={classes.detailTabs}
          >
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              {/* The count comes from the provider row that opened this page,
                  so it is whatever the list showed in its Documents column —
                  hidden rather than shown as 0 when nothing is on file. */}
              <Tabs.Tab
                value="documents"
                rightSection={
                  data?.documentCount ? (
                    <span className={classes.tabCount}>{data.documentCount}</span>
                  ) : null
                }
              >
                Documents
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview">
              <Box className={classes.tabsPanel}>
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Box className={classes.provForm}>
                    <Grid gutter={16}>
                      <Grid.Col span={6}>
                        <TextInput
                          label="Admin First Name"
                          placeholder="Enter First Name"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("firstName")}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <TextInput
                          label="Admin Last Name"
                          placeholder="Enter Last Name"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("lastName")}
                        />
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <TextInput
                          label="Business Name"
                          placeholder="Enter Business Name"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("businessName")}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <TextInput
                          label="Email"
                          placeholder="Enter Email"
                          className={classes.provInputWrap}
                          readOnly
                          {...form.getInputProps("email")}
                        />
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <TextInput
                          label="Phone Number"
                          placeholder="Enter Phone Number"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("phone")}
                        />
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <TextInput
                          label="Address 1"
                          placeholder="Enter Address 1"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("address")}
                        />
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <TextInput
                          label="Address 2"
                          placeholder="Enter Address 2 (Optional)"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("address2")}
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <TextInput
                          label="City"
                          placeholder="Enter City"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("city")}
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Select
                          label="State"
                          rightSection={<IconChevronDown color={"#9CA3AF"} size={16} />}
                          rightSectionWidth={30}
                          placeholder="Select State"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          data={Array.from(
                            new Set(stateZipCodes?.map((obj) => obj.ste_name))
                          )
                            .sort()
                            .map((ste_name) => ({ value: ste_name, label: ste_name }))}
                          searchable
                          {...form.getInputProps("state")}
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <TextInput
                          label="Zipcode"
                          placeholder="Enter Zipcode"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("zipcode")}
                        />
                      </Grid.Col>
                      {isExternalProvider && (
                        <>
                          <Grid.Col span={form.values.ehrSystem === "Other" ? 6 : 12}>
                            <Select
                              label="EHR System"
                              rightSection={<IconChevronDown color={"#9CA3AF"} size={16} />}
                              rightSectionWidth={30}
                              placeholder="Select EHR"
                              className={classes.provInputWrap}
                              readOnly={isViewMode}
                              data={ehrOptions.map((ehr) => ({ value: ehr, label: ehr }))}
                              {...form.getInputProps("ehrSystem")}
                            />
                          </Grid.Col>
                          {form.values.ehrSystem === "Other" && (
                            <Grid.Col span={6}>
                              <TextInput
                                label="EHR Name"
                                placeholder="Enter EHR system name"
                                className={classes.provInputWrap}
                                readOnly={isViewMode}
                                {...form.getInputProps("ehrSystemOther")}
                              />
                            </Grid.Col>
                          )}
                        </>
                      )}
                      <Grid.Col span={12}>
                        <TextInput
                          label="NPI # (National Provider Identifier)"
                          placeholder="Enter 10-digit NPI Number"
                          className={classes.provInputWrap}
                          readOnly={isViewMode}
                          {...form.getInputProps("npi")}
                        />
                      </Grid.Col>
                      {/* Notes captured in the Add Provider drawer are read
                          back (and edited) here — otherwise they would be
                          write-only. Hidden in view mode when empty rather
                          than showing an empty box. */}
                      {(!isViewMode || form.values.providerNotes) && (
                        <Grid.Col span={12}>
                          <Textarea
                            label="Notes"
                            placeholder="Anything useful about this provider…"
                            className={cx(
                              classes.provInputWrap,
                              classes.provTextareaWrap
                            )}
                            minRows={3}
                            maxRows={8}
                            autosize
                            readOnly={isViewMode}
                            {...form.getInputProps("providerNotes")}
                          />
                        </Grid.Col>
                      )}
                      {isViewMode && !isExternalProvider && (
                        <>
                          <Grid.Col span={4}>
                            <Text fz={14} fw={500} color="#344054" ff="Inter" mb={8}>
                              Number of facilities
                            </Text>
                            <Text className={classes.countBox}>{data?.facilityCount}</Text>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Text fz={14} fw={500} color="#344054" ff="Inter" mb={8}>
                              Number of clients
                            </Text>
                            <Text className={classes.countBox}>{data?.clientCount}</Text>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Text fz={14} fw={500} color="#344054" ff="Inter" mb={8}>
                              Number of staff
                            </Text>
                            <Text className={classes.countBox}>{data?.staffCount}</Text>
                          </Grid.Col>
                        </>
                      )}
                    </Grid>
                    {mode === "edit" && (
                      <Flex className={classes.btnGroup}>
                        <Button
                          variant="outline"
                          className={classes.cancelBtn}
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" loading={loading} className={classes.createBtn}>
                          Edit Provider
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </form>
              </Box>
            </Tabs.Panel>

            <Tabs.Panel value="documents">
              <Box className={classes.tabsPanel}>
                <ProviderDocuments
                  providerId={data?.id || data?._id}
                  providerName={data?.businessName || data?.name || "Provider"}
                  canManage={!isStaff(profile?.user?.role)}
                  initialView={state?.documentView}
                />
              </Box>
            </Tabs.Panel>
          </Tabs>
        </>
      )}
      </Box>
    </Box>
  );
};

export default SkypointAddProvider;
